(() => {
  const ROOT_SELECTOR = 'variant-selects[data-ready-size-picker="true"]';
  const SIZE_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*(?:"|in(?:ches)?)?\s*[wW]\s*(?:[xX*×]\s*)?(\d+(?:\.\d+)?)\s*(?:"|in(?:ches)?)?\s*[hH]\s*$/i;

  const parseSize = (value) => {
    const match = String(value || '').match(SIZE_PATTERN);
    return match ? { width: match[1], height: match[2] } : null;
  };

  const numberSort = (a, b) => Number(a) - Number(b);

  const mount = (root) => {
    if (root.dataset.readySizeMounted === 'true') return;

    const nativeSelects = [...root.querySelectorAll('select[name^="options["]')];
    const sizeSelect = nativeSelects.find((select) =>
      [...select.options].some((option) => parseSize(option.value))
    );
    if (!sizeSelect) return;

    const parsedOptions = [...sizeSelect.options]
      .map((option) => ({ option, size: parseSize(option.value) }))
      .filter((entry) => entry.size);
    if (!parsedOptions.length || parsedOptions.length !== sizeSelect.options.length) return;

    const variantsNode = root.querySelector('[data-ready-size-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      return;
    }

    const sizeIndex = nativeSelects.indexOf(sizeSelect);
    if (sizeIndex < 0) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'ab-size-picker';
    wrapper.setAttribute('data-ready-size-ui', '');

    const makeField = (labelText, name) => {
      const field = document.createElement('div');
      field.className = 'ab-size-picker__field';
      const label = document.createElement('label');
      label.className = 'ab-size-picker__label';
      const select = document.createElement('select');
      const id = `${root.id}-${name}`;
      label.htmlFor = id;
      label.textContent = labelText;
      select.id = id;
      select.className = 'form-control form-control--select ab-size-picker__select';
      select.setAttribute('aria-label', labelText);
      field.append(label, select);
      wrapper.append(field);
      return select;
    };

    const widthSelect = makeField('Width (inches)', 'width');
    const heightSelect = makeField('Height (inches)', 'height');
    const nativeControl = sizeSelect.closest('.product-form__input') || sizeSelect.parentElement;

    const selectedSize = parseSize(sizeSelect.value);
    const widths = [...new Set(parsedOptions.map(({ size }) => size.width))].sort(numberSort);
    widths.forEach((width) => widthSelect.add(new Option(`${width}"`, width)));

    const otherSelections = () =>
      nativeSelects.map((select) => select.value);

    const matchingVariantAvailable = (sizeValue) => {
      const selections = otherSelections();
      selections[sizeIndex] = sizeValue;
      return variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((value, index) => value === selections[index])
      );
    };

    const renderHeights = (preferredHeight) => {
      const matching = parsedOptions.filter(({ size }) => size.width === widthSelect.value);
      const heights = [...new Set(matching.map(({ size }) => size.height))].sort(numberSort);
      heightSelect.replaceChildren();

      heights.forEach((height) => {
        const entry = matching.find(({ size }) => size.height === height);
        const available = matchingVariantAvailable(entry.option.value);
        const option = new Option(`${height}"${available ? '' : ' — Sold out'}`, height);
        option.disabled = !available;
        heightSelect.add(option);
      });

      const preferred = [...heightSelect.options].find(
        (option) => option.value === preferredHeight && !option.disabled
      );
      const fallback = [...heightSelect.options].find((option) => !option.disabled);
      if (preferred) heightSelect.value = preferred.value;
      else if (fallback) heightSelect.value = fallback.value;
    };

    const syncNativeSize = () => {
      const entry = parsedOptions.find(
        ({ size }) => size.width === widthSelect.value && size.height === heightSelect.value
      );
      if (!entry || entry.option.disabled || !matchingVariantAvailable(entry.option.value)) return;
      if (sizeSelect.value !== entry.option.value) {
        const selectedAttributeOption = sizeSelect.querySelector('option[selected]');
        if (selectedAttributeOption) {
          selectedAttributeOption.setAttribute('selected', 'selected');
        } else {
          sizeSelect.selectedOptions[0]?.setAttribute('selected', 'selected');
        }
        sizeSelect.value = entry.option.value;
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    widthSelect.value = selectedSize?.width || widths[0];
    renderHeights(selectedSize?.height);
    nativeControl.before(wrapper);
    nativeControl.classList.add('ab-native-size-control--enhanced');
    nativeControl.setAttribute('aria-hidden', 'true');

    widthSelect.addEventListener('change', (event) => {
      event.stopPropagation();
      renderHeights('');
      syncNativeSize();
    });
    heightSelect.addEventListener('change', (event) => {
      event.stopPropagation();
      syncNativeSize();
    });

    nativeSelects
      .filter((select) => select !== sizeSelect)
      .forEach((select) =>
        select.addEventListener('change', () => {
          const current = parseSize(sizeSelect.value);
          if (current) widthSelect.value = current.width;
          renderHeights(current?.height);
          syncNativeSize();
        })
      );

    root.dataset.readySizeMounted = 'true';
  };

  const mountAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) mount(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(mount);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
  } else {
    mountAll();
  }

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) =>
      addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) mountAll(node);
      })
    );
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
